import { Button, Container, Grid2 as Grid, TextField } from "@mui/material"
import { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { NexAlert } from "../../components/Alert/Alert"
import { NexDropzone } from "../../components/Dropzone"
import { LoadingButton } from "../../components/LoadingButton/LoadingButton"
import { ProjectTitle } from "../../components/Titles/ProjectTitle"
import { Label } from "../../components/Typography/Typography"
import { useNexusProjectNumber } from "../../hooks/useNexusParams"
import { useTitle } from "../../hooks/useTitle"
import { routes } from "../../routing/NexRoutes"
import { useFlightplanEditStore } from "../../zstore/FlightplanEditStore"
import { useStyles } from "./styles"

export const FlightplanUpload = () => {
    useTitle("Upload Flightplan")
    const navigate = useNavigate()
    const { classes, cx } = useStyles()
    const projectNumber = useNexusProjectNumber()

    const parseFlightplanFile = useFlightplanEditStore((state) => state.parseFlightplanFile)
    const fetchError = useFlightplanEditStore((state) => state.fetchError)
    const resetFlightplanStore = useFlightplanEditStore((state) => state.reset)
    const clearError = useFlightplanEditStore((state) => state.clearError)

    const [file, setFile] = useState<File | null>(null)
    const [flightplanName, setFlightplanName] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        resetFlightplanStore()
    }, [])

    const handleChangeFile = (newFiles: File[]) => {
        const file = newFiles[0]
        if (!file) {
            return
        }
        const { name } = file

        setFile(newFiles[0] || null)
        setFlightplanName(name.split(".")[0])
    }

    const handleNameChange = (value: string | null) => {
        setFlightplanName(value)
    }

    const uploadFile = async () => {
        setLoading(true)
        if (!file || !flightplanName) {
            throw new Error("No file or flightplan name")
        }
        await parseFlightplanFile(projectNumber, flightplanName, file)
        navigate(`/${projectNumber}${routes.flightplanEditor}/submit`)

        setLoading(false)
    }

    return (
        <Container maxWidth="md" className={classes.root}>
            <ProjectTitle projectNumber={projectNumber} />

            <Grid className={classes.gutterBottom__md}>
                <Label gutterBottom="sm">Flightplan Upload</Label>
            </Grid>

            <NexDropzone
                acceptedFileTypes={[".mdb", ".fpd3", ".zip", ".kml", ".kmz"]}
                filesLimit={1}
                onChange={handleChangeFile}
            />

            <Label gutterBottom="sm">New Flightplan Name</Label>
            <Grid>
                <TextField
                    variant="outlined"
                    value={flightplanName || ""}
                    onChange={(e) => handleNameChange(e.target.value || null)}
                    className={cx(classes.gutterBottom__lg, classes.input__lg, classes.text_field)}
                />
            </Grid>

            {fetchError && (
                <NexAlert animate onClose={clearError}>
                    {fetchError}
                </NexAlert>
            )}

            <Grid container justifyContent="space-between" spacing={2}>
                <Grid>
                    <Button component={Link} to={routes.home} variant="outlined" color="primary" size="large">
                        Back
                    </Button>
                </Grid>
                <Grid>
                    <LoadingButton
                        onClick={uploadFile}
                        pending={loading}
                        variant="contained"
                        color="primary"
                        size="large"
                        disabled={!file || !flightplanName}
                    >
                        Upload File
                    </LoadingButton>
                </Grid>
            </Grid>
        </Container>
    )
}
